import React, { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Search, CheckCircle2, AlertTriangle, Ban, DoorClosed, CheckSquare, Square, XCircle, Sparkles, Info } from "lucide-react";
import { inspectionsApi, doorsApi, buildingsApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { apiError } from "@/lib/format";
import {
  INITIAL_CHECKLIST, QUESTION_TEXT, SINGLE_GAP_FIELDS, DOUBLE_GAP_FIELDS, THRESHOLD_GAP_KEYS,
  FIRE_RATINGS, LEAF_OPTIONS, gapIsFail, isItemFailed, visibleItems, calculateStatus,
  buildInspectionPayload, parseReasonBullets,
} from "@/lib/inspectionSchema";
import { FAILURE_REASON_GROUPS, suggestReasonGroupKeys, isAdvisoryReason } from "@/pdf/failureReasons";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PhotoCapture } from "@/components/PhotoCapture";
import { PrivacyNotice } from "@/components/PrivacyNotice";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function Segmented({ value, options, onChange, testId }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 p-0.5" data-testid={testId}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          onClick={() => onChange(o.value)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            value === o.value ? o.tone || "bg-primary text-white" : "text-slate-500 hover:text-slate-800"
          }`}
          data-testid={testId ? `${testId}-${o.label}` : undefined}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const YNA = [
  { label: "Yes", value: "Yes", tone: "bg-emerald-600 text-white" },
  { label: "No", value: "No", tone: "bg-red-600 text-white" },
  { label: "N/A", value: "N/A", tone: "bg-slate-600 text-white" },
];
const SWITCH_OPTS = [
  { label: "Yes", value: true, tone: "bg-emerald-600 text-white" },
  { label: "No", value: false, tone: "bg-red-600 text-white" },
];

function Section({ title, subtitle, children }) {
  return (
    <Card className="p-5">
      <div className="mb-4">
        <h3 className="font-semibold text-slate-800">{title}</h3>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
      {children}
    </Card>
  );
}

// ── Door picker (only when no doorId in the URL) ─────────────────────
function DoorPicker({ onPick, initialBuildingId, restrictDoorIdsStr }) {
  const [q, setQ] = useState("");
  const [building, setBuilding] = useState(null);
  const buildings = useAsync(() => buildingsApi.getAll(), []);
  // When launched from a scheduled inspection, jump straight to that building.
  React.useEffect(() => {
    if (initialBuildingId && !building) {
      buildingsApi.getOne(initialBuildingId).then((r) => { if (r.data) setBuilding(r.data); }).catch(() => {});
    }
  }, [initialBuildingId]);
  const doors = useAsync(() => (building ? doorsApi.getAll(building.id) : Promise.resolve({ data: [] })), [building?.id]);
  const list = useMemo(() => {
    const arr = Array.isArray(buildings.data) ? buildings.data : [];
    const s = q.trim().toLowerCase();
    return s ? arr.filter((b) => `${b.name} ${b.address} ${b.postcode}`.toLowerCase().includes(s)).slice(0, 25) : arr.slice(0, 25);
  }, [buildings.data, q]);
  // Restrict to the booked door subset when provided (falls back to all doors
  // if none of the booked ids resolve on this building).
  const { doorList, showingBooked, bookedCount } = useMemo(() => {
    const arr = Array.isArray(doors.data) ? doors.data : [];
    const ids = (restrictDoorIdsStr || "").split(",").map((x) => x.trim()).filter(Boolean);
    if (ids.length === 0) return { doorList: arr, showingBooked: false, bookedCount: 0 };
    const set = new Set(ids);
    const restricted = arr.filter((d) => set.has(d.id));
    return restricted.length > 0
      ? { doorList: restricted, showingBooked: true, bookedCount: restricted.length }
      : { doorList: arr, showingBooked: false, bookedCount: 0 };
  }, [doors.data, restrictDoorIdsStr]);

  return (
    <Section title="Select a door" subtitle={showingBooked ? "These doors were booked for this visit — pick one to inspect." : "Choose the building, then the door you are inspecting."}>
      {!building ? (
        <div>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search buildings…" className="pl-9" data-testid="picker-building-search" />
          </div>
          <div className="max-h-80 space-y-1.5 overflow-auto">
            {list.map((b) => (
              <button key={b.id} type="button" onClick={() => setBuilding(b)} className="flex w-full items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-left text-sm hover:bg-slate-50" data-testid={`picker-building-${b.id}`}>
                <span className="truncate text-slate-700">{b.name}</span>
                <span className="shrink-0 text-xs text-slate-400">{b.door_count ?? 0} doors</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <button type="button" onClick={() => setBuilding(null)} className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
            <ArrowLeft className="h-4 w-4" /> {building.name}
          </button>
          {showingBooked && (
            <div className="mb-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-700" data-testid="picker-booked-note">
              Showing the {bookedCount} door{bookedCount === 1 ? "" : "s"} booked for this visit. Tap “{building.name}” above to see every door.
            </div>
          )}
          {doors.loading ? (
            <p className="py-4 text-center text-sm text-slate-400">Loading doors…</p>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {doorList.map((d) => (
                <button key={d.id} type="button" onClick={() => onPick(d.id, building.id)} className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-2 text-left text-sm hover:bg-slate-50" data-testid={`picker-door-${d.id}`}>
                  <DoorClosed className="h-4 w-4 text-slate-400" />
                  <span className="truncate text-slate-700">{d.door_reference || d.location}</span>
                </button>
              ))}
              {doorList.length === 0 && <p className="py-4 text-center text-sm text-slate-400">No doors on this building.</p>}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

export default function InspectionForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id: editId } = useParams();
  const [sp] = useSearchParams();
  const [doorId, setDoorId] = useState(sp.get("doorId") || null);
  const [buildingId, setBuildingId] = useState(sp.get("buildingId") || null);

  const door = useAsync(() => (doorId ? doorsApi.getOne(doorId) : Promise.resolve({ data: null })), [doorId]);
  const existing = useAsync(() => (editId ? inspectionsApi.getOne(editId) : Promise.resolve({ data: null })), [editId]);

  // ── Form state ─────────────────────────────────────────────────────
  const [checklist, setChecklist] = useState(() => INITIAL_CHECKLIST.map((x) => ({ ...x })));
  const [spyViewerFireRated, setSpyViewerFireRated] = useState("N/A");
  const [gaps, setGaps] = useState({});
  const [doorLeafType, setDoorLeafType] = useState("single");
  const [isFireDoor, setIsFireDoor] = useState(true);
  const [fireRating, setFireRating] = useState("Unknown");
  const [letterboxOpeningSize, setLetterboxOpeningSize] = useState("");
  const [letterboxFailureReason, setLetterboxFailureReason] = useState("");
  const [failures, setFailures] = useState({});
  const [failureReasons, setFailureReasons] = useState({});
  const [failureOther, setFailureOther] = useState({});
  const [customReasons, setCustomReasons] = useState({});
  const [failureLeaf, setFailureLeaf] = useState({});
  const [labelPhoto, setLabelPhoto] = useState("");
  const [photos, setPhotos] = useState([]);
  const [additionalObservations, setAdditionalObservations] = useState("");
  const [notes, setNotes] = useState("");
  const [quoteNotes, setQuoteNotes] = useState("");
  const [signature, setSignature] = useState("");
  const [inspectorName, setInspectorName] = useState("");
  const [inspectionDate, setInspectionDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [noAccess, setNoAccess] = useState(false);
  const [noAccessNote, setNoAccessNote] = useState("");
  const [nextFreq, setNextFreq] = useState("12_month");
  const [amendReason, setAmendReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Default leaf type / rating from the door record.
  React.useEffect(() => {
    const d = door.data;
    if (d && !editId) {
      if (d.door_leaf_type === "double") setDoorLeafType("double");
      if (d.fd_rating && FIRE_RATINGS.includes(d.fd_rating)) setFireRating(d.fd_rating);
      setNextFreq(d.inspection_frequency || (d.door_type === "communal" ? "3_month" : "12_month"));
    }
    if (d && !buildingId) setBuildingId(d.building_id);
  }, [door.data]);

  // Hydrate for edit mode.
  React.useEffect(() => {
    const insp = existing.data;
    if (!insp || hydrated) return;
    const cd = insp.checklist_data || {};
    const items = cd.custom_items || {};
    setChecklist(INITIAL_CHECKLIST.map((x) => ({ ...x, value: items[x.key] !== undefined ? items[x.key] : x.value })));
    setSpyViewerFireRated(cd.spy_viewer_fire_rated || "N/A");
    setGaps(cd.gap_measurements || {});
    setDoorLeafType(cd.door_leaf_type === "double" ? "double" : "single");
    setIsFireDoor(cd.is_fire_door !== false);
    setFireRating(cd.fire_rating || "Unknown");
    setLetterboxOpeningSize(cd.letterbox_opening_size || "");
    setLetterboxFailureReason(cd.letterbox_failure_reason || "");
    setAdditionalObservations(cd.additional_observations || "");
    setNotes(insp.notes || "");
    setQuoteNotes(insp.quote_notes || "");
    // Keep the ORIGINAL inspector's name on amend. A drawn (data-URL)
    // signature can't be shown in a text box, so fall back to the name.
    const sig = insp.signature || "";
    setSignature(String(sig).startsWith("data:") ? (insp.inspector_name || "") : sig);
    setInspectorName(insp.inspector_name || "");
    setInspectionDate(String(insp.inspection_date || "").slice(0, 10) || new Date().toISOString().slice(0, 10));
    setNoAccess(!!cd.no_access);
    setNoAccessNote(cd.no_access_note || "");
    const f = {}, fr = {}, cr = {}, fl = {};
    (insp.failed_component_photos || []).forEach((fc) => {
      f[fc.key] = { photos: fc.photos || (fc.photo ? [fc.photo] : []) };
      const reasons = Array.isArray(fc.reasons) && fc.reasons.length ? fc.reasons.map(String) : parseReasonBullets(fc.notes);
      fr[fc.key] = reasons;
      const groupKeys = suggestReasonGroupKeys(fc.question || QUESTION_TEXT[fc.key] || "");
      const canned = new Set();
      FAILURE_REASON_GROUPS.filter((g) => groupKeys.includes(g.key)).forEach((g) => g.reasons.forEach((r) => canned.add(r)));
      cr[fc.key] = reasons.filter((r) => !canned.has(r));
      if (fc.leaf === "leaf1" || fc.leaf === "leaf2" || fc.leaf === "both") fl[fc.key] = fc.leaf;
    });
    setFailures(f);
    setFailureReasons(fr);
    setCustomReasons(cr);
    setFailureLeaf(fl);
    setLabelPhoto(insp.label_photo || "");
    setPhotos(insp.photos || []);
    if (!doorId && insp.door_id) setDoorId(insp.door_id);
    setHydrated(true);
  }, [existing.data]);

  // Prefill the inspector name + typed signature from the signed-in
  // user's profile on a NEW inspection (amend keeps the original).
  React.useEffect(() => {
    if (editId || !user?.name) return;
    setInspectorName((prev) => prev || user.name);
    setSignature((prev) => prev || user.name);
  }, [editId, user?.name]);

  const setAnswer = (key, value) => {
    setChecklist((prev) => {
      let next = prev.map((it) => (it.key === key ? { ...it, value } : it));
      if (key === "letterbox_present" && value !== "Yes") next = next.map((it) => (it.key === "letterbox_fire_rated" || it.key === "letterbox_operates_correctly" ? { ...it, value: "N/A" } : it));
      if (key === "letterbox_fire_rated" && value !== "Yes") next = next.map((it) => (it.key === "letterbox_operates_correctly" ? { ...it, value: "N/A" } : it));
      return next;
    });
  };
  const setGap = (key, v) => setGaps((prev) => ({ ...prev, [key]: v }));
  const setFailure = (key, patch) => setFailures((prev) => ({ ...prev, [key]: { photos: [], ...prev[key], ...patch } }));
  const toggleReason = (key, reason) => setFailureReasons((prev) => {
    const cur = prev[key] || [];
    return { ...prev, [key]: cur.includes(reason) ? cur.filter((r) => r !== reason) : [...cur, reason] };
  });
  const addCustomReason = (key) => {
    const text = (failureOther[key] || "").trim();
    if (!text) return;
    setCustomReasons((prev) => {
      const cur = prev[key] || [];
      if (cur.some((r) => r.toLowerCase() === text.toLowerCase())) return prev;
      return { ...prev, [key]: [...cur, text] };
    });
    setFailureReasons((prev) => {
      const cur = prev[key] || [];
      return cur.includes(text) ? prev : { ...prev, [key]: [...cur, text] };
    });
    setFailureOther((prev) => ({ ...prev, [key]: "" }));
  };
  const removeCustomReason = (key, reason) => {
    setCustomReasons((prev) => ({ ...prev, [key]: (prev[key] || []).filter((r) => r !== reason) }));
    setFailureReasons((prev) => ({ ...prev, [key]: (prev[key] || []).filter((r) => r !== reason) }));
  };

  const answers = useMemo(() => Object.fromEntries(checklist.map((i) => [i.key, i.value])), [checklist]);
  const dropdownSealWorks = answers.dropdown_seal === "Yes";
  const activeGapFields = [...SINGLE_GAP_FIELDS, ...(doorLeafType === "double" ? DOUBLE_GAP_FIELDS : [])];
  const shownGapFields = activeGapFields.filter((f) => !(dropdownSealWorks && THRESHOLD_GAP_KEYS.includes(f.key)));
  const perimeterFail = activeGapFields.filter((f) => !THRESHOLD_GAP_KEYS.includes(f.key)).some((f) => gapIsFail(gaps[f.key]));
  const thresholdFail = !dropdownSealWorks && activeGapFields.filter((f) => THRESHOLD_GAP_KEYS.includes(f.key)).some((f) => gapIsFail(gaps[f.key]));
  const gapWithinTolerance = !perimeterFail;
  const thresholdGapWithinTolerance = dropdownSealWorks ? true : !thresholdFail;
  const shownChecklist = visibleItems(checklist);

  const computedStatus = noAccess ? "no_access" : calculateStatus({ checklist, gaps, gapWithinTolerance, thresholdGapWithinTolerance, noAccess, isFireDoor });
  const statusMeta = {
    pass: { label: "Satisfactory", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
    review: { label: "Further review required", cls: "bg-amber-100 text-amber-700", Icon: AlertTriangle },
    fail: { label: "Remedial action required", cls: "bg-red-100 text-red-700", Icon: AlertTriangle },
    no_access: { label: "No access", cls: "bg-slate-200 text-slate-700", Icon: Ban },
  }[computedStatus];

  const submit = async (overrideStatus) => {
    if (!doorId) { toast.error("Please select a door first"); return; }
    setSaving(true);
    const state = {
      checklist, spyViewerFireRated, gaps, gapWithinTolerance, thresholdGapWithinTolerance,
      doorLeafType, isFireDoor, fireRating, letterboxOpeningSize, letterboxFailureReason,
      failures, failureReasons, failureOther, customReasons, failureLeaf,
      labelPhoto, photos, additionalObservations, notes, quoteNotes, signature, inspectorName, inspectionDate, noAccess, noAccessNote,
    };
    const payload = buildInspectionPayload(state, doorId, noAccess ? undefined : overrideStatus);
    payload.inspection_frequency = nextFreq;
    try {
      if (editId) {
        payload.amend_reason = amendReason || "Amended via web";
        await inspectionsApi.update(editId, payload);
        toast.success("Inspection updated");
        navigate(`/inspections/${editId}`);
      } else {
        const res = await inspectionsApi.create(payload);
        toast.success("Inspection saved");
        navigate(`/inspections/${res.data.id}`);
      }
    } catch (e) {
      toast.error(apiError(e, "Could not save inspection"));
      setSaving(false);
    }
  };

  if (editId && existing.loading) return <div className="mx-auto max-w-3xl"><LoaderRow /></div>;

  const d = door.data;

  return (
    <div className="mx-auto max-w-3xl pb-28">
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <PageHeader
        title={editId ? "Amend inspection" : "New inspection"}
        subtitle={d ? `${d.door_reference || d.location || "Door"}${d.building_name ? ` · ${d.building_name}` : ""}` : "Fire door inspection (BS 8214)"}
        actions={statusMeta && <Badge className={statusMeta.cls}><statusMeta.Icon className="mr-1 h-3.5 w-3.5" />{statusMeta.label}</Badge>}
        testId="inspection-form-header"
      />

      {!doorId ? (
        <DoorPicker
          onPick={(dId, bId) => { setDoorId(dId); setBuildingId(bId); }}
          initialBuildingId={buildingId}
          restrictDoorIdsStr={sp.get("doorIds") || ""}
        />
      ) : (
        <div className="space-y-5">
          {/* No access */}
          <Section title="Access">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm text-slate-600">Could you access and inspect this door?</span>
              <Segmented value={!noAccess} options={[{ label: "Yes — inspect", value: true, tone: "bg-emerald-600 text-white" }, { label: "No access", value: false, tone: "bg-slate-700 text-white" }]} onChange={(v) => setNoAccess(!v)} testId="no-access-toggle" />
            </div>
            {noAccess && (
              <div className="mt-3">
                <Label>Reason / observations</Label>
                <Textarea value={noAccessNote} onChange={(e) => setNoAccessNote(e.target.value)} placeholder="e.g. Resident not home, second attempt" className="mt-1" data-testid="no-access-note" />
              </div>
            )}
          </Section>

          {!noAccess && (
            <>
              {/* Door setup */}
              <Section title="Door">
                <div className="space-y-4">
                  <Field label="Is this a fire door?">
                    <Segmented value={isFireDoor} options={SWITCH_OPTS} onChange={setIsFireDoor} testId="is-fire-door" />
                  </Field>
                  <Field label="Door leaf type">
                    <Segmented value={doorLeafType} options={[{ label: "Single", value: "single" }, { label: "Double", value: "double" }]} onChange={setDoorLeafType} testId="leaf-type" />
                  </Field>
                  <Field label="Fire rating">
                    <div className="flex flex-wrap gap-1.5">
                      {FIRE_RATINGS.map((r) => (
                        <button key={r} type="button" onClick={() => setFireRating(r)} className={`rounded-full px-3 py-1 text-sm font-medium ${fireRating === r ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`fire-rating-${r}`}>{r}</button>
                      ))}
                    </div>
                  </Field>
                </div>
              </Section>

              {/* Checklist */}
              <Section title="Inspection checklist" subtitle="BS 8214 — 19 checks">
                <div className="divide-y divide-slate-100">
                  {shownChecklist.map((it) => (
                    <div key={it.key} className="py-3">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <span className={`text-sm ${isItemFailed(it) ? "font-medium text-red-600" : "text-slate-700"}`}>{it.question}</span>
                        {it.type === "switch" ? (
                          <Segmented value={it.value} options={SWITCH_OPTS} onChange={(v) => setAnswer(it.key, v)} testId={`q-${it.key}`} />
                        ) : (
                          <Segmented value={it.value} options={YNA} onChange={(v) => setAnswer(it.key, v)} testId={`q-${it.key}`} />
                        )}
                      </div>
                      {isItemFailed(it) && (
                        <div className="mt-3" data-testid={`failure-inline-${it.key}`}>
                          <FailureBlock
                            item={it}
                            isDouble={doorLeafType === "double"}
                            photos={(failures[it.key] || {}).photos || []}
                            onPhotos={(ph) => setFailure(it.key, { photos: ph })}
                            reasons={failureReasons[it.key] || []}
                            customs={customReasons[it.key] || []}
                            otherText={failureOther[it.key] || ""}
                            onOther={(v) => setFailureOther((p) => ({ ...p, [it.key]: v }))}
                            onAdd={() => addCustomReason(it.key)}
                            onToggle={(r) => toggleReason(it.key, r)}
                            onRemoveCustom={(r) => removeCustomReason(it.key, r)}
                            leaf={failureLeaf[it.key] || "both"}
                            onLeaf={(v) => setFailureLeaf((p) => ({ ...p, [it.key]: v }))}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                  {answers.spy_viewer === "Yes" && (
                    <div className="flex flex-wrap items-center justify-between gap-3 py-3 pl-4">
                      <span className="text-sm text-slate-600">Is the spy viewer fire-rated?</span>
                      <Segmented value={spyViewerFireRated} options={YNA} onChange={setSpyViewerFireRated} testId="q-spy_viewer_fire_rated" />
                    </div>
                  )}
                  {answers.letterbox_present === "Yes" && (
                    <div className="space-y-3 py-3 pl-4">
                      <Field label="Letterbox opening size">
                        <Input value={letterboxOpeningSize} onChange={(e) => setLetterboxOpeningSize(e.target.value)} placeholder="e.g. 250mm x 40mm" className="max-w-xs" data-testid="letterbox-size" />
                      </Field>
                      {(answers.letterbox_fire_rated === "No" || answers.letterbox_operates_correctly === "No") && (
                        <Field label="Letterbox failure reason">
                          <Input value={letterboxFailureReason} onChange={(e) => setLetterboxFailureReason(e.target.value)} placeholder="e.g. Mechanism stuck open" className="max-w-md" data-testid="letterbox-reason" />
                        </Field>
                      )}
                    </div>
                  )}
                </div>
              </Section>

              {/* Gap measurements */}
              <Section title="Gap measurements (mm)" subtitle="BS 8214 tolerance 2–4 mm. Anything over 4 mm fails.">
                {dropdownSealWorks && (
                  <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                    ✅ Dropdown seal is intact and working — bottom-of-door threshold gaps are not required.
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {shownGapFields.map((f) => {
                    const fail = gapIsFail(gaps[f.key]);
                    return (
                      <div key={f.key}>
                        <Label className={`text-xs ${fail ? "text-red-600" : "text-slate-500"}`}>{fail ? "⚠ " : ""}{f.label}</Label>
                        <Input inputMode="decimal" value={gaps[f.key] || ""} onChange={(e) => setGap(f.key, e.target.value)} placeholder="—" className={`mt-1 ${fail ? "border-red-400 bg-red-50" : ""}`} data-testid={`gap-${f.key}`} />
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 flex flex-wrap gap-4 text-sm">
                  <span className={gapWithinTolerance ? "text-emerald-600" : "text-red-600"}>Perimeter: {gapWithinTolerance ? "within tolerance" : "over tolerance"}</span>
                  {!dropdownSealWorks && <span className={thresholdGapWithinTolerance ? "text-emerald-600" : "text-red-600"}>Threshold: {thresholdGapWithinTolerance ? "within tolerance" : "over tolerance"}</span>}
                </div>
              </Section>

              {/* Photos */}
              <Section title="Photos">
                <PrivacyNotice className="mb-3" testId="photo-privacy-notice">
                  Photos are stored securely as inspection evidence and are visible to your organisation and the building&apos;s Responsible Person. Avoid capturing people or personal belongings where you can.
                </PrivacyNotice>
                <Field label="Fire door label photo">
                  <PhotoCapture photos={labelPhoto ? [labelPhoto] : []} onChange={(ph) => setLabelPhoto(ph[0] || "")} max={1} testId="label-photo" />
                </Field>
                <div className="mt-4">
                  <Field label="General documentation photos">
                    <PhotoCapture photos={photos} onChange={setPhotos} max={12} testId="general-photos" />
                  </Field>
                </div>
              </Section>

              {/* Notes */}
              <Section title="Notes">
                <div className="space-y-4">
                  <Field label="Additional observations">
                    <Textarea value={additionalObservations} onChange={(e) => setAdditionalObservations(e.target.value)} placeholder="Any extra observations…" data-testid="additional-observations" />
                  </Field>
                  <Field label="Inspector notes (on client report)">
                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="inspector-notes" />
                  </Field>
                  <Field label="Quote notes (internal — never on the client PDF)">
                    <Textarea value={quoteNotes} onChange={(e) => setQuoteNotes(e.target.value)} data-testid="quote-notes" />
                  </Field>
                </div>
              </Section>
            </>
          )}

          {/* Signature */}
          <Section title="Sign-off">
            <Field label="Inspection date">
              <Input type="date" value={inspectionDate} onChange={(e) => setInspectionDate(e.target.value)} className="max-w-xs" data-testid="inspection-date" />
            </Field>
            <div className="mt-3">
              <Field label="Inspector name">
                <Input value={inspectorName} onChange={(e) => setInspectorName(e.target.value)} placeholder="Your name" className="max-w-xs" data-testid="inspector-name" />
              </Field>
            </div>
            <div className="mt-3">
              <Label>Signature (type your full name)</Label>
              <Input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Type your full name to sign" className="mt-1 max-w-sm italic" data-testid="signature-input" />
              <PrivacyNotice className="mt-1.5" testId="esign-notice">
                By signing you confirm this inspection record is accurate. Your name and the sign-off date are recorded with this signature as an audit record.
              </PrivacyNotice>
            </div>
            {editId && (
              <div className="mt-3">
                <Field label="Reason for amendment">
                  <Input value={amendReason} onChange={(e) => setAmendReason(e.target.value)} placeholder="Why is this being changed?" className="max-w-md" data-testid="amend-reason" />
                </Field>
              </div>
            )}
          </Section>

          {/* Submit bar — manual outcome override (mirrors mobile) */}
          <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur lg:pl-72">
            <div className="mx-auto max-w-3xl">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Re-inspect in</span>
                <Segmented
                  value={nextFreq}
                  onChange={setNextFreq}
                  testId="next-inspection-frequency"
                  options={[
                    { value: "3_month", label: "3 months" },
                    { value: "6_month", label: "6 months" },
                    { value: "12_month", label: "12 months" },
                  ]}
                />
              </div>
              {noAccess ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm"><span className="text-slate-400">Result: </span><span className="font-semibold text-slate-800">No access</span></div>
                  <Button onClick={() => submit()} disabled={saving} size="lg" data-testid="submit-inspection">
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Save as No Access
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid="auto-classified-banner">
                    <Sparkles className="h-3.5 w-3.5 text-slate-400" />
                    <span className="font-semibold uppercase tracking-wide text-slate-400">Auto-classified:</span>
                    <span className={`font-bold ${computedStatus === "fail" ? "text-red-600" : computedStatus === "review" ? "text-amber-600" : "text-emerald-600"}`}>{statusMeta?.label}</span>
                    <span className="text-slate-400">— choose the outcome to save:</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button onClick={() => submit("pass")} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-compliant">
                      {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                      {editId ? "Save as Satisfactory" : "Satisfactory"}
                    </Button>
                    <Button onClick={() => submit("fail")} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="submit-needs-attention">
                      {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <AlertTriangle className="mr-2 h-4 w-4" />}
                      {editId ? "Save as Needs Attention" : "Needs Attention"}
                    </Button>
                    {computedStatus === "review" && (
                      <Button onClick={() => submit("pass_with_observations")} disabled={saving} className="bg-sky-500 hover:bg-sky-600" data-testid="submit-pass-with-observations">
                        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Info className="mr-2 h-4 w-4" />}
                        Pass with observations
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <Label className="text-slate-600">{label}</Label>
      {children}
    </div>
  );
}

function FailureBlock({ item, isDouble, photos, onPhotos, reasons, customs, otherText, onOther, onAdd, onToggle, onRemoveCustom, leaf, onLeaf }) {
  const groupKeys = suggestReasonGroupKeys(item.question);
  const groups = FAILURE_REASON_GROUPS.filter((g) => groupKeys.includes(g.key));
  return (
    <div className="rounded-lg border border-red-100 bg-red-50/50 p-4" data-testid={`failure-${item.key}`}>
      <p className="mb-3 text-sm font-semibold text-red-700">{item.question}</p>

      {isDouble && (
        <div className="mb-3" data-testid={`failure-leaf-${item.key}`}>
          <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-amber-700">Which leaf?</p>
          <div className="flex flex-wrap gap-2">
            {LEAF_OPTIONS.map((o) => {
              const active = (leaf || "both") === o.key;
              return (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => onLeaf(o.key)}
                  data-testid={`failure-leaf-${item.key}-${o.key}`}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${active ? "border-amber-600 bg-amber-600 text-white" : "border-amber-300 bg-white text-amber-800 hover:bg-amber-50"}`}
                >
                  {o.label} <span className={active ? "text-amber-100" : "text-amber-500"}>· {o.hint}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-lg border-l-2 border-amber-400 bg-amber-50 p-3">
        <p className="text-xs font-bold uppercase tracking-wide text-amber-700">Why is it failing? (tap all that apply)</p>
        {groups.map((g) => (
          <div key={g.key} className="mt-2">
            <p className="text-sm font-semibold text-slate-800">{g.label}</p>
            {g.reasons.map((r) => {
              const checked = reasons.includes(r);
              const advisory = isAdvisoryReason(r);
              return (
                <button key={r} type="button" onClick={() => onToggle(r)} data-testid={`failure-reason-${item.key}`} className="flex w-full items-start gap-2 py-1.5 text-left">
                  {checked ? <CheckSquare className={`mt-0.5 h-4 w-4 shrink-0 ${advisory ? "text-blue-600" : "text-emerald-600"}`} /> : <Square className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />}
                  <span className="text-sm text-slate-700">
                    {advisory && <span className="mr-1.5 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-700">Advisory</span>}
                    {r}
                  </span>
                </button>
              );
            })}
          </div>
        ))}

        {customs.length > 0 && (
          <div className="mt-3 border-t border-amber-200 pt-2">
            <p className="text-sm font-semibold text-amber-700">Your custom answers</p>
            {customs.map((r, idx) => {
              const checked = reasons.includes(r);
              return (
                <div key={r} className="flex items-start gap-2 py-1.5">
                  <button type="button" onClick={() => onToggle(r)} className="flex flex-1 items-start gap-2 text-left">
                    {checked ? <CheckSquare className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <Square className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />}
                    <span className="text-sm text-slate-700">{r}</span>
                  </button>
                  <button type="button" onClick={() => onRemoveCustom(r)} data-testid={`failure-reason-custom-remove-${item.key}-${idx}`} className="text-red-500 hover:text-red-700"><XCircle className="h-4 w-4" /></button>
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-3 text-sm font-semibold text-slate-800">Add your own answer</p>
        <p className="mb-1.5 text-xs italic text-amber-700">Type a defect, tap Add — it becomes a tickable answer here AND lands in the Price Log for an admin to price.</p>
        <div className="flex gap-2">
          <Input
            value={otherText}
            onChange={(e) => onOther(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }}
            placeholder="e.g. Lock barrel replacement"
            className="bg-white"
            data-testid={`failure-reason-other-input-${item.key}`}
          />
          <Button type="button" onClick={onAdd} disabled={!otherText.trim()} data-testid={`failure-reason-other-add-${item.key}`}>Add</Button>
        </div>
      </div>

      <div className="mt-3">
        <p className="mb-1.5 text-xs font-medium text-slate-500">Photos of issue</p>
        <PhotoCapture photos={photos} onChange={onPhotos} max={5} testId={`failure-photo-${item.key}`} />
      </div>
    </div>
  );
}

function LoaderRow() {
  return (
    <div className="flex items-center justify-center py-20 text-slate-400">
      <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
    </div>
  );
}
